// codes_manager.jsx
// Every QR code generated across the product, with the owner attached, and a
// per-code scan rollup behind each row. Clicking the owner jumps to their
// account in the users tab.

import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  QrCode, Search, RefreshCw, ScanLine, User, Image as ImageIcon, Link2,
} from 'lucide-react';
import { useQRCreate } from '../../authentication_components/qrCreateContext';
import {
  Section, Modal, Button, Loading, EmptyState, Pagination, Badge, ScanRollup,
  inputClass, fmtInt, fmtDate, fmtDateTime,
} from './qrcreate_ui';

const PAGE_SIZE = 25;

const SORTS = [
  { id: 'created', label: 'Newest first' },
  { id: 'scans', label: 'Most scanned' },
  { id: 'last_scan', label: 'Recently scanned' },
  { id: 'name', label: 'Name' },
];

const TIERS = [
  { value: '', label: 'Any tier' },
  { value: 'true', label: 'Customized (credit-funded)' },
  { value: 'false', label: 'Plain (free quota)' },
];

const STATES = [
  { value: '', label: 'Any state' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

export default function CodesManager({ onOpenUser }) {
  const { fetchCodes, fetchScanFilters } = useQRCreate();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [qrType, setQrType] = useState('');
  const [customized, setCustomized] = useState('');
  const [active, setActive] = useState('');
  const [sort, setSort] = useState('created');
  const [offset, setOffset] = useState(0);

  const [data, setData] = useState({ codes: [], total: 0 });
  const [qrTypes, setQrTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => { setOffset(0); setSearch(searchInput.trim()); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    fetchScanFilters()
      .then((options) => setQrTypes(options?.qr_types || []))
      .catch(() => { /* the dropdown simply stays minimal */ });
  }, [fetchScanFilters]);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const result = await fetchCodes({
        search: search || undefined,
        qr_type: qrType || undefined,
        customized: customized || undefined,
        active: active || undefined,
        sort,
        order: sort === 'name' ? 'asc' : 'desc',
        limit: PAGE_SIZE,
        offset,
      });
      setData({ codes: result?.codes || [], total: result?.total || 0 });
    } catch (err) {
      toast.error(err.message || 'Failed to load generated codes');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [fetchCodes, search, qrType, customized, active, sort, offset]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => { setRefreshing(true); load({ silent: true }); };

  return (
    <div className="mx-auto max-w-7xl space-y-5 px-3 py-6 sm:px-4 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-800">Generated QR codes</h2>
          <p className="text-sm text-slate-500">Every code created in the app, who owns it and how often it gets scanned.</p>
        </div>
        <Button variant="secondary" onClick={refresh} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <Section title="Codes" icon={QrCode} className="p-0">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-4">
          <div className="relative min-w-[240px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search code name, destination or owner email…"
              className={`${inputClass} pl-9`}
            />
          </div>
          {qrTypes.length > 0 && (
            <select value={qrType} onChange={(e) => { setOffset(0); setQrType(e.target.value); }} className={`${inputClass} w-36`}>
              <option value="">Any type</option>
              {qrTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
          <select value={customized} onChange={(e) => { setOffset(0); setCustomized(e.target.value); }} className={`${inputClass} w-52`}>
            {TIERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <select value={active} onChange={(e) => { setOffset(0); setActive(e.target.value); }} className={`${inputClass} w-36`}>
            {STATES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select value={sort} onChange={(e) => { setOffset(0); setSort(e.target.value); }} className={`${inputClass} w-44`}>
            {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>

        {loading && data.codes.length === 0 ? (
          <Loading label="Loading codes…" />
        ) : data.codes.length === 0 ? (
          <EmptyState
            icon={QrCode}
            title="No codes match these filters"
            action={<Button variant="secondary" onClick={() => { setSearchInput(''); setQrType(''); setCustomized(''); setActive(''); }}>Clear filters</Button>}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Code</th>
                    <th className="px-4 py-3">Owner</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3 text-right">Scans</th>
                    <th className="px-4 py-3">Last scan</th>
                    <th className="px-4 py-3">Created</th>
                    <th className="px-4 py-3 text-right">Detail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {data.codes.map((c) => (
                    <tr key={c.generation_id} className="hover:bg-sky-50/40">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-800">{c.name}</span>
                          {c.is_customized
                            ? <Badge className="bg-sky-50 text-sky-700 ring-sky-200">Customized</Badge>
                            : <Badge className="bg-slate-100 text-slate-600 ring-slate-200">Plain</Badge>}
                          {!c.is_active && <Badge className="bg-rose-50 text-rose-700 ring-rose-200">Inactive</Badge>}
                        </div>
                        <div className="max-w-sm truncate text-xs text-slate-400" title={c.target_url}>{c.target_url}</div>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => onOpenUser?.(c.user_id)}
                          className="group text-left"
                          title="Open this account"
                        >
                          <div className="font-medium text-slate-700 group-hover:text-sky-700">{c.owner_name || '(no name)'}</div>
                          <div className="text-xs text-slate-400 group-hover:text-sky-600">{c.owner_email}</div>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{c.qr_type}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-700">{fmtInt(c.scan_count)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-500">{fmtDate(c.last_scanned_at)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-500">{fmtDate(c.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setSelected(c)}
                          className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-sky-50 hover:text-sky-600"
                          title="View scan analytics"
                        >
                          <ScanLine className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination total={data.total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} noun="code" />
          </>
        )}
      </Section>

      <CodeDetailModal code={selected} onClose={() => setSelected(null)} onOpenUser={onOpenUser} />
    </div>
  );
}

function CodeDetailModal({ code, onClose, onOpenUser }) {
  const { fetchCodeScans } = useQRCreate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!code) { setStats(null); return; }
    let cancelled = false;
    setLoading(true);
    fetchCodeScans(code.generation_id, { days: 30 })
      .then((result) => { if (!cancelled) setStats(result || null); })
      .catch((err) => { if (!cancelled) toast.error(err.message || 'Failed to load scan analytics'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [code, fetchCodeScans]);

  return (
    <Modal open={!!code} title={code?.name || 'QR code'} subtitle={code?.target_url} onClose={onClose} wide>
      {!code ? null : (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="sm:col-span-2 space-y-3">
              <Row label="Owner">
                <button onClick={() => { onOpenUser?.(code.user_id); onClose(); }} className="inline-flex items-center gap-1.5 font-medium text-sky-700 hover:underline">
                  <User className="h-3.5 w-3.5" /> {code.owner_name || code.owner_email || code.user_id}
                </button>
              </Row>
              <Row label="Destination">
                <a href={code.target_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 break-all text-sky-700 hover:underline">
                  <Link2 className="h-3.5 w-3.5 shrink-0" /> {code.target_url}
                </a>
              </Row>
              <Row label="Scan link">
                <code className="break-all rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">{code.qr_uuid}</code>
              </Row>
              <Row label="Tier">
                {code.is_customized ? `Customized · ${fmtInt(code.credit_cost)} credit${code.credit_cost === 1 ? '' : 's'}` : 'Plain (free quota)'}
                {code.show_splash && <span className="text-slate-400"> · shows splash screen</span>}
              </Row>
              {code.template_name && <Row label="Template">{code.template_name}</Row>}
              <Row label="Created">{fmtDateTime(code.created_at)}</Row>
              {code.expires_at && <Row label="Expires">{fmtDateTime(code.expires_at)}</Row>}
            </div>

            <div className="flex items-start justify-center">
              {code.image_url ? (
                <a href={code.image_url} target="_blank" rel="noreferrer" title="Open full size">
                  <img src={code.image_url} alt={code.name} className="h-40 w-40 rounded-xl border border-slate-200 object-contain p-2" />
                </a>
              ) : (
                <div className="flex h-40 w-40 flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 text-slate-300">
                  <ImageIcon className="h-8 w-8" />
                  <span className="mt-1 text-xs">No render stored</span>
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4">
            {loading ? <Loading label="Loading scan analytics…" /> : <ScanRollup stats={stats} />}
          </div>
        </div>
      )}
    </Modal>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex flex-wrap items-baseline gap-2 text-sm">
      <span className="w-28 shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <span className="min-w-0 flex-1 text-slate-700">{children}</span>
    </div>
  );
}
